/**
 * `/genres/:genreId` — TASK 10.3 Step 2.
 *
 * The mock's `_genre(V,id)` view model + template, wired to
 * `GET /api/genres/:id` (TASK 4.3). Deliberately the same shape as
 * ../browse/ArtistDetail.js — both are a backlink + heading + paged
 * `AlbumGridCard` grid — and it REUSES that module's card and its
 * `albumYearLabel` rather than re-deriving either.
 *
 * ── AC 15.2: ARTIST-THEN-YEAR, NO-YEAR LAST, NEVER "null" ─────────────────
 * The SERVER sorts (routes/genres.ts: "artist then year ascending, no-year
 * albums LAST with the year key OMITTED"), and the client does NOT re-sort —
 * ArtistDetail.js's reasoning applies verbatim: the order is global across
 * keyset pages, so sorting each arriving page locally would interleave page
 * 2's early entries back above page 1's late ones. The "no null-year label"
 * half of the AC is `albumYearLabel` (imported, not copied): it prints a year
 * ONLY for an actual finite number, so neither `undefined` nor a hypothetical
 * `null` can ever reach `String()` and paint the words "null"/"undefined"
 * (ui-contract §10.6, `alb-lost` "Lost Sessions" reaches this grid via Load
 * more on `gen-alternativerock`).
 *
 * ── BOUNDS + PAGINATION ───────────────────────────────────────────────────
 * ui-contract §10.6 scale table: "/genres/:id · 475 albums · 24 ·
 * 'Load more ↓' (`load-more-button`) · +24" — `ALBUM_GRID_PAGE_SIZE` is
 * imported from ArtistDetail.js, whose own comment names this route as the
 * source of the 24.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 10.3's files */

import { ERROR_CODES, TESTIDS } from '@streamymusic/shared';

import { VirtualList } from '../../components/VirtualList.js';
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  NotFoundBlock,
} from '../../components/StateBlocks.js';
import { FavoriteMarker } from '../../components/primitives/Card.js';
import { append, el } from '../../components/primitives/dom.js';
import { ALBUM_GRID_PAGE_SIZE, AlbumGridCard } from '../browse/ArtistDetail.js';

/** The genre page's empty-grid copy (a genre with zero remaining albums). */
export const GENRE_EMPTY_MESSAGE = 'No albums in this genre yet.';

/** The 404 copy — a path back, NO Retry (ui-contract §10.6). */
export const GENRE_NOT_FOUND_MESSAGE = 'No genre with that id — it may have been removed.';

/**
 * Builds the `/genres/:id` section and starts loading its first page.
 *
 * @param {{
 *   api: {
 *     getGenreAlbums: (
 *       genreId: string,
 *       options?: import('../../../api/api-client.js').PageOptions,
 *     ) => Promise<import('@streamymusic/shared').GenreAlbumsResponse>,
 *   },
 *   heading: HTMLElement,
 *   genreId: string,
 *   getState: () => import('../../app/state-store.js').AppState,
 *   subscribe: (listener: (next: import('../../app/state-store.js').AppState) => void) => () => void,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   onAlbumMenu?: (album: import('@streamymusic/shared').AlbumCard) => void,
 *   pageSize?: number,
 *   depth?: import('../../../lib/list-depth.js').ListDepth,
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 *     the app's diagnostics seam (modules/app/index.js). A SERVER contract
 *     violation detected while deciding an availability marker is reported
 *     here rather than only to the user's devtools console.
 * }} options
 *   `api` is typed structurally rather than `Pick<ApiClient, 'getGenreAlbums'>`
 *   because the client method lands with this task's wiring — the structural
 *   type IS the signature the api-client must satisfy (`GET /api/genres/:id`,
 *   parsed by `genreAlbumsResponseSchema`, shaped exactly like
 *   `getArtistAlbums`). `getState`/`subscribe` are the store seam
 *   ArtistDetail.js already uses: favourite state is READ AT PAINT TIME and
 *   kept live by subscription, so a ♥ toggled from this page's own ⋯ sheet
 *   (or rolled back by a failed write) repaints the affected card without a
 *   navigation.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function GenrePage(options) {
  const {
    api,
    heading,
    genreId,
    getState,
    subscribe,
    offline = false,
    downloadedAlbums = [],
    onAlbumMenu,
    pageSize = ALBUM_GRID_PAGE_SIZE,
    depth,
    announce,
    warn,
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Genre detail' } });

  const backLink = append(
    el('p', { className: 'sm-backlink' }),
    el('a', {
      className: 'sm-backlink__a',
      text: '← All genres',
      testId: TESTIDS.ALL_GENRES_LINK,
      attrs: { href: '#/browse/genres' },
    }),
  );

  const countLabel = el('p', {
    className: 'sm-count-label sm-count-label--detail',
    attrs: { hidden: true },
  });
  const body = el('div');
  const notice = el('div');
  append(section, backLink, heading, countLabel, body, notice);

  const downloads = new Set(downloadedAlbums);

  /** @type {import('@streamymusic/shared').AlbumCard[]} */
  let items = [];
  /** @type {string|null} */
  let cursor = null;
  let generation = 0;
  let disposed = false;
  /** Cancels the in-flight page fetch on dispose (and on a superseding load). */
  /** @type {AbortController|null} */
  let controller = null;
  /** Pages held, so the depth memory can replay them on return (#back). */
  let pagesLoaded = 0;

  /** The depth this route was left at, read ONCE before the first paint. */
  const restored = depth?.read() ?? null;

  /**
   * Mounted cards, keyed by album id, with the ♥ state they were painted
   * with — the same targeted-repaint mechanism as ArtistDetail's
   * `cardsByAlbumId`: held explicitly so a favourite toggle touches ONLY the
   * cards whose state flipped, never the grid.
   *
   * @type {Map<string, { root: HTMLElement, favorited: boolean }>}
   */
  const cardsByAlbumId = new Map();

  const list = VirtualList({
    items,
    windowSize: pageSize,
    renderItem: (album) => {
      const favorited = getState().catalog.favorites.albums.includes(album.id);
      const root = AlbumGridCard({
        album,
        surface: 'browse',
        favorite: favorited,
        offline,
        downloaded: downloads.has(album.id),
        ...(warn === undefined ? {} : { onContractViolation: warn }),
        ...(onAlbumMenu === undefined ? {} : { onMenu: onAlbumMenu }),
      });
      cardsByAlbumId.set(album.id, { root, favorited });
      return root;
    },
    loadMoreLabel: 'Load more ↓',
    // ui-contract §3 enumerates `load-more-button` on THIS route ("Load
    // more ↓" on /genres/gen-jazz); the id is DERIVED from the accessible
    // name, so /artists/:id's grid — same affordance, same name — lands on
    // the same id. The two live on different routes and can never share a
    // DOM; a §3 Tier-1 selector still resolves exactly one control.
    loadMoreTestId: TESTIDS.LOAD_MORE_BUTTON,
    listClassName: 'sm-grid-covers',
    ...(restored === null ? {} : { initialShown: restored.shown }),
    onWindowChange: (shown) => depth?.remember(pagesLoaded, shown),
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  /** The favourites array the cards were last painted against (identity). */
  let paintedFavAlbums = getState().catalog.favorites.albums;

  /**
   * The C14 targeted repaint: on a store tick, early-out unless the favourite
   * ALBUM set actually changed (the transport patches `pos` ~4×/s — none of
   * those may touch the grid), then toggle the ♥ marker on only the cards
   * whose state flipped.
   */
  function syncFromStore() {
    const favAlbums = getState().catalog.favorites.albums;
    if (favAlbums === paintedFavAlbums) return;
    paintedFavAlbums = favAlbums;
    const favoriteSet = new Set(favAlbums);
    for (const [albumId, entry] of cardsByAlbumId) {
      const favorited = favoriteSet.has(albumId);
      if (favorited === entry.favorited || !entry.root.isConnected) continue;
      entry.favorited = favorited;
      const marker = entry.root.querySelector('.sm-cover__fav');
      if (favorited && marker === null) append(entry.root, FavoriteMarker());
      else if (!favorited && marker !== null) marker.remove();
    }
  }

  const unsubscribe = subscribe(() => {
    if (disposed) return;
    syncFromStore();
  });

  /**
   * @param {{ append?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const own = (generation += 1);
    // A superseded request is CANCELLED, not merely ignored: the generation
    // guard already discards its settle, aborting stops the transfer too.
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage) {
      items = [];
      cursor = null;
      pagesLoaded = 0;
      cardsByAlbumId.clear();
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 4, label: 'Loading albums', variant: 'cover' }));
    }

    try {
      const page = await api.getGenreAlbums(genreId, {
        limit: pageSize,
        ...(appendPage && cursor !== null ? { cursor } : {}),
        signal: ownController.signal,
      });
      if (disposed || own !== generation) return;

      heading.textContent = page.genre.name;
      // Server order preserved verbatim (AC 15.2 — see the module doc).
      items = appendPage ? [...items, ...page.items] : [...page.items];
      cursor = page.nextCursor;
      pagesLoaded += 1;

      if (items.length === 0) {
        body.replaceChildren(
          EmptyBlock({
            message: GENRE_EMPTY_MESSAGE,
            showSyncLink: false,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        return;
      }

      const n = items.length;
      countLabel.textContent = `${n.toLocaleString()}${cursor === null ? '' : '+'} album${n === 1 ? '' : 's'}`;
      countLabel.removeAttribute('hidden');

      paintedFavAlbums = getState().catalog.favorites.albums;
      list.update({ items, hasMore: cursor !== null });
      if (body.firstChild !== list.element) body.replaceChildren(list.element);
      depth?.remember(pagesLoaded, list.shownCount());
    } catch (error) {
      if (disposed || own !== generation) return;
      // Branch on the MACHINE-READABLE code (api-client.ts `ApiError.code`,
      // ERROR_CODES), not the transport status: the status is how the code
      // travelled, the code is what it means. An unknown genre id is
      // NOT_FOUND and NOT retriable (ui-contract §10.6: "path back, no
      // Retry") — routes/genres.ts has no 410 for a genre (a genre with no
      // remaining albums answers an EMPTY page, not GONE).
      const code = /** @type {{ code?: string }} */ (error).code;
      if (code === ERROR_CODES.NOT_FOUND && !appendPage) {
        heading.textContent = 'Genre';
        body.replaceChildren(NotFoundBlock({ message: GENRE_NOT_FOUND_MESSAGE }));
        return;
      }
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        // A failed NEXT page must not discard the pages already on screen.
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  /**
   * First paint, re-opening the depth the user left (#back). Every replayed
   * page is a hit on the api-client's 24h per-cursor catalog cache, so this
   * costs no network — see `lib/list-depth.ts`.
   *
   * @returns {Promise<void>}
   */
  async function loadToRememberedDepth() {
    await load();
    if (restored === null) return;
    for (let page = 1; page < restored.pages; page += 1) {
      if (disposed || cursor === null) return;
      const before = items.length;
      await load({ append: true });
      if (items.length === before) return; // page failed or added nothing
    }
  }

  void loadToRememberedDepth();

  return {
    element: section,
    dispose() {
      // Cancels every in-flight paint: the next settle sees `disposed` and
      // writes nothing into a section the shell has already replaced. The
      // abort additionally cancels the TRANSFER, and the unsubscribe drops
      // the store listener so it cannot outlive the section.
      disposed = true;
      controller?.abort();
      unsubscribe();
      list.dispose();
    },
  };
}
